import { useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const fullUrl = (u) => (u && u.startsWith("/api/") ? `${BACKEND_URL}${u}` : u);

export default function DressGallery({ images = [], alt = "" }) {
  const [active, setActive] = useState(0);
  const safe = (images || []).filter(Boolean).map(fullUrl);

  if (safe.length === 0) {
    return (
      <div className="relative aspect-[3/4] bg-stone-50 overflow-hidden flex items-center justify-center text-ink/30 font-display text-4xl">
        Kein Bild
      </div>
    );
  }

  if (safe.length === 1) {
    return (
      <div data-testid="dress-gallery" className="relative aspect-[3/4] bg-stone-50 overflow-hidden">
        <img
          src={safe[0]}
          alt={alt}
          className="absolute inset-0 w-full h-full object-contain"
          loading="lazy"
          decoding="async"
        />
      </div>
    );
  }

  const safeActive = Math.min(active, safe.length - 1);
  const goPrev = () => setActive((i) => (i - 1 + safe.length) % safe.length);
  const goNext = () => setActive((i) => (i + 1) % safe.length);

  return (
    <div data-testid="dress-gallery" className="flex flex-col gap-4">
      {/* Main image */}
      <div className="relative aspect-[3/4] bg-stone-50 overflow-hidden group">
        <img
          src={safe[safeActive]}
          alt={`${alt} – Bild ${safeActive + 1}`}
          className="absolute inset-0 w-full h-full object-contain"
          key={safeActive}
          loading="eager"
          decoding="async"
        />

        <button
          type="button"
          data-testid="gallery-prev"
          onClick={goPrev}
          aria-label="Vorheriges Bild"
          className="absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 bg-cream/95 backdrop-blur border border-[#E5E0D8] hover:bg-ink hover:text-cream transition-colors flex items-center justify-center z-10"
        >
          <ChevronLeft size={18} />
        </button>
        <button
          type="button"
          data-testid="gallery-next"
          onClick={goNext}
          aria-label="Nächstes Bild"
          className="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 bg-cream/95 backdrop-blur border border-[#E5E0D8] hover:bg-ink hover:text-cream transition-colors flex items-center justify-center z-10"
        >
          <ChevronRight size={18} />
        </button>
        <div className="absolute bottom-3 right-3 bg-ink/85 text-cream px-2 py-1 text-[10px] tracking-widest uppercase z-10">
          {safeActive + 1} / {safe.length}
        </div>
      </div>

      {/* Thumbnail strip — only as many columns as images, max 5 */}
      <div
        className="grid gap-2"
        style={{ gridTemplateColumns: `repeat(${Math.min(safe.length, 5)}, minmax(0, 1fr))` }}
      >
        {safe.slice(0, 5).map((src, i) => (
          <button
            key={i}
            type="button"
            data-testid={`gallery-thumb-${i}`}
            onClick={() => setActive(i)}
            className={`relative aspect-[3/4] overflow-hidden bg-stone-50 border-2 transition-all ${
              i === safeActive ? "border-gold" : "border-transparent opacity-70 hover:opacity-100"
            }`}
            aria-label={`Bild ${i + 1} anzeigen`}
          >
            <img src={src} alt={`Thumbnail ${i + 1}`} className="absolute inset-0 w-full h-full object-contain" loading="lazy" decoding="async" />
          </button>
        ))}
      </div>
    </div>
  );
}
